/* -- Image component ----------------------------------------- */
/* Fallback on error, progressive sources (data-src-low /        */
/* data-src-high) + lightbox preview for [data-preview].         */
/* Named-state API bound per figure so agents/tests can show     */
/* the fallback without a network failure (AGENTS.md "State       */
/* API").                                                        */

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
// defussQuery: the callable runtime - error flag + lightbox control writes
// ride query scalars, the sheet mounts through query .append(); the lightbox
// template is a trusted static markup string (§3 image row, §5.1: lightbox
// content re-renders are the real morph path when added - the toolbar is a
// static singleton, so it stays a one-shot render).
import { defussGlobals, defussQuery, componentState, bindComponent } from '../../../shared/state-api.js';

const df$ = defussGlobals();
const dfDollar = defussQuery();

const imageStates = ['default', 'error'];

// VERIFIED: (verify's API docs gate) the states below are exactly the declared ones, each
// described, and every config field typed, described and named in the code.
/** setState() configs per state - the image's states take none. */
export interface ImageStateConfigs {
  /** The image shows. */
  default: {};
  /** The image failed to load - the fallback shows (set by the error event, or by setState). */
  error: {};
}

/**
 * The markup of a state, for render(): the attributes a state writes, applied
 * to a detached copy of the authored markup ('default' IS the authored
 * markup). The live element gets the same markup from triggerStateChange -
 * the e2e render round trip proves they agree.
 */
function applyMarkup(el, stateName) {
  dfDollar(el).find('img').first().attr('data-error', stateName === 'error' ? '' : null);
}

/**
 * UI side of setState (per figure): 'error' marks the img like a failed load
 * would (CSS then reveals .image-fallback); 'default' clears the mark.
 */
function triggerStateChange(figure, stateName, _config) {
  const img = dfDollar(figure).find('img')[0];
  if (!img) return;
  // data-error is the CSS fallback marker - set/removed via query data scalars
  switch (stateName) {
    case 'default':
      dfDollar(img).data('error', null);
      break;
    case 'error':
      dfDollar(img).data('error', '');
      break;
  }
}

/** Registry-level API; pass the figure explicitly. Unknown names throw. */
export const imageApi = componentState({
  component: 'image',
  states: imageStates,
  apply: (figure, state) => triggerStateChange(figure, state.name, state.config),
  read: (figure, state) => {
    // reflect reality: load/error events flip it without setState()
    const img = dfDollar(figure).find('img')[0];
    return {
      name: img && dfDollar(img).data('error') !== undefined ? 'error' : 'default',
      config: state.config,
    };
  },
  markup: (el, state) => applyMarkup(el, state.name),
});

df$.imageApi = imageApi;
df$.imageStates = imageStates;


/* -- Hover gallery: preload + decode before switching -------------------
   A hover gallery shows its images instantly as the pointer crosses the
   strips - an image that is still downloading or not yet decoded would
   flash the first one through. So every image loads eagerly, and once the
   gallery comes near the viewport all of them are decoded (img.decode());
   only then does it switch (data-ready - until then the first stays). */
const galleryIO = typeof IntersectionObserver === 'function'
  ? new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        galleryIO.unobserve(e.target);
        readyGallery(e.target);
      }
    }, { rootMargin: '300px' })
  : null;
function readyGallery(gallery) {
  const imgs = [...dfDollar(gallery).find<HTMLImageElement>(':scope > img, :scope > picture img').toArray()];
  Promise.all(imgs.map((img) => (img.decode ? img.decode() : Promise.resolve()).catch(() => undefined))).then(() => {
    gallery.dataset.ready = '';
  });
}
function initHoverGalleries() {
  dfDollar('.hover-gallery:not([data-init])').toArray().forEach((gallery) => {
    gallery.dataset.init = '';
    dfDollar(gallery).find<HTMLImageElement>(':scope > img, :scope > picture img').toArray().forEach((img, i) => {
      if (img.loading === 'lazy') img.loading = 'eager';
      if (i > 0 && !img.hasAttribute('fetchpriority')) img.fetchPriority = 'low';
    });
    if (galleryIO) galleryIO.observe(gallery);
    else readyGallery(gallery);
  });
}

function init() {
initHoverGalleries();
/* -- Fallback: mark images that fail to load ----------------- */
dfDollar('.image:not([data-init])').toArray().forEach((figure) => {
  figure.dataset.init = '';
  // el.store + el.api (AGENTS.md "State through stores")
  bindComponent(figure, imageApi);
  const img = dfDollar(figure).find('img')[0];
  if (!img) return;

  if (img.complete && img.naturalWidth === 0) {
    dfDollar(img).data('error', '');
  }

  img.addEventListener('error', () => {
    dfDollar(img).data('error', '');
    figure.dataset.stateName = 'error'; // State API marker stays dataset.*
  });

  img.addEventListener('load', () => {
    dfDollar(img).data('error', null);
    figure.dataset.stateName = 'default';
  });

  /* -- Progressive sources (data-src-low / data-src-high) -------
     Why: data-src-low paints a cheap placeholder while the real
     source preloads off-DOM (new Image()); data-src-high is a
     retina-only upgrade - paying its bytes on 1dppx displays (or
     before a lightbox zoom-in) would waste bandwidth. */
  const srcLow = img.dataset.srcLow;
  const srcHigh = img.dataset.srcHigh;
  if (srcLow || srcHigh) {
    const retina = !!srcHigh && globalThis.matchMedia('(min-resolution: 2dppx)').matches;
    const finalSrc = retina ? srcHigh : img.getAttribute('src');
    if (retina) img.dataset.srcHighLoaded = ''; // first zoom-in must not re-fetch
    if (srcLow && finalSrc) {
      dfDollar(img).data('loading', ''); // CSS blurs the placeholder
      img.src = srcLow;
      const preload = new Image();
      // either way the img gets the real source - its own load/error
      // listeners above then settle the State API marker
      preload.onload = preload.onerror = () => {
        img.src = finalSrc;
        dfDollar(img).data('loading', null);
      };
      preload.src = finalSrc;
    } else if (retina) {
      img.src = srcHigh;
    }
  }
});
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });

/* -- Lightbox ------------------------------------------------ */
let lightbox = null;
let lightboxImg = null;
let lightboxFigure = null; // figure the lightbox is showing (high-res upgrade target)
let zoom = 1;
let rotation = 0;

function getLightbox() {
  if (lightbox) return lightbox;

  lightbox = document.createElement('dialog');
  lightbox.className = 'image-lightbox';
  lightbox.setAttribute('aria-label', 'Image preview');

  // trusted static toolbar markup through query's .html() (§5.1: sanctioned
  // render path - a static singleton template, no user content)
  dfDollar(lightbox).html(`
    <div class="image-lightbox-content">
      <img src="" alt="" />
    </div>
    <div class="image-lightbox-toolbar">
      <button class="image-lightbox-btn" data-action="zoom-in" aria-label="Zoom in">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
      </button>
      <button class="image-lightbox-btn" data-action="zoom-out" aria-label="Zoom out">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
      </button>
      <button class="image-lightbox-btn" data-action="rotate-left" aria-label="Rotate left">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 2v6h6"/><path d="M2.66 15.57a10 10 0 1 0 .57-8.38"/></svg>
      </button>
      <button class="image-lightbox-btn" data-action="rotate-right" aria-label="Rotate right">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6"/><path d="M21.34 15.57a10 10 0 1 1-.57-8.38"/></svg>
      </button>
      <button class="image-lightbox-btn" data-action="reset" aria-label="Reset">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
      </button>
      <button class="image-lightbox-btn" data-action="close" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>`);

  lightboxImg = dfDollar(lightbox).find('.image-lightbox-content > img')[0];

  /* Toolbar actions */
  dfDollar(lightbox).find('.image-lightbox-toolbar')[0].addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!btn) return;

    const action = btn.dataset.action;
    if (action === 'zoom-in') {
      zoom = Math.min(zoom + 0.25, 5);
      upgradeLightboxToHigh(); // the first zoom earns the high-res bytes
    }
    else if (action === 'zoom-out') zoom = Math.max(zoom - 0.25, 0.25);
    else if (action === 'rotate-left')  rotation -= 90;
    else if (action === 'rotate-right') rotation += 90;
    else if (action === 'reset') { zoom = 1; rotation = 0; }
    else if (action === 'close') { lightbox.close(); return; }

    applyTransform();
  });

  /* Close on backdrop click */
  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) lightbox.close();
  });

  dfDollar(document.body).append(lightbox); // query's exact mount op
  return lightbox;
}

/* Why: the high-res source earns its bytes only once the user actually
   zooms - the first zoom-in swaps lightbox AND figure img, never sooner. */
function upgradeLightboxToHigh() {
  if (!lightboxFigure) return;
  const img = dfDollar(lightboxFigure).find('img')[0];
  if (img && img.dataset.srcFull) return; // the lightbox already shows the original
  const srcHigh = img && img.dataset.srcHigh;
  if (!srcHigh || img.dataset.srcHighLoaded !== undefined) return;
  img.dataset.srcHighLoaded = '';
  img.src = srcHigh;
  if (lightboxImg) lightboxImg.src = srcHigh;
}

function applyTransform() {
  if (lightboxImg) {
    dfDollar(lightboxImg).css('transform', `scale(${zoom}) rotate(${rotation}deg)`);
  }
}

function openLightbox(figure) {
  const lb = getLightbox();
  zoom = 1;
  rotation = 0;
  const img = dfDollar(figure).find('img')[0];
  lightboxFigure = figure;
  const $img = dfDollar(lightboxImg);
  // consumer-provided src flows in via <img> attributes already (§5.2: the
  // figure's own src/alt are the trusted source, attr writes mirror them)
  $img.attr('src', img.src).attr('alt', img.alt || '').css('transform', null).css('width', null);
  /* data-src-full: the lightbox-only original (much larger than the inline
     src). Why: the page should never pay for it - only an actual preview
     does. The cached inline src opens instantly, pre-sized to the fitted
     frame the original will fill; the original preloads off-DOM and swaps
     in without a layout jump (the width lock is released on swap). */
  const srcFull = img.dataset.srcFull;
  if (srcFull) {
    const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0;
    if (ratio) $img.css('width', `min(90vw, calc(85vh * ${ratio.toFixed(4)}))`);
    const full = new Image();
    full.onload = () => {
      if (lightboxFigure !== figure || !lb.open) return; // closed / another image meanwhile
      $img.attr('src', srcFull).css('width', null);
    };
    full.src = srcFull;
  }
  lb.showModal(); // native dialog protocol stays native
}

/* -- Attach preview click handlers --------------------------- */
if (!document.__imagePreviewInit) {
  document.__imagePreviewInit = true;

  document.addEventListener('click', (e) => {
    const figure = (e.target as HTMLElement).closest<HTMLElement>('.image[data-preview]');
    if (!figure) return;

    const img = dfDollar(figure).find('img')[0];
    if (!img || dfDollar(img).data('error') !== undefined) return;

    openLightbox(figure);
  });
}
